/**
 * Health Honeycomb View — enterprise health-by-group visualization
 * Card per group (Collection/Folder/Platform), hex per device.
 * Per-card metric filter: CPU | RAM | Storage | Alerts | Baseline
 *
 * Preserves existing props, grouping, metric logic, click handlers,
 * localStorage collection/folder usage, and view modes.
 */
import React, { useState, useMemo, memo, useCallback } from 'react';
import {
  Layers,
  Cpu,
  MemoryStick,
  HardDrive,
  AlertTriangle,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import { STORAGE_COLLECTIONS, STORAGE_FOLDERS } from '../constants/storageKeys';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';

const VIEW_OPTIONS = [
  { id: 'collection', label: 'By Collection' },
  { id: 'folder', label: 'By Folder' },
  { id: 'platform', label: 'By Platform' },
];

const METRIC_OPTIONS = [
  { id: 'cpu', label: 'CPU', icon: Cpu },
  { id: 'ram', label: 'RAM', icon: MemoryStick },
  { id: 'storage', label: 'Storage', icon: HardDrive },
  { id: 'status', label: 'Alerts', icon: AlertTriangle },
  { id: 'baseline', label: 'Baseline', icon: ShieldCheck },
];

const MAX_HEXES_PER_CARD = 60;
const HEALTHY_SAMPLE_IN_PREVIEW = 10;
const LARGE_GROUP_THRESHOLD = 200;
const GRID_DIAMOND_THRESHOLD = 5;
const HEX_CLIP = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

const STATUS_PRIORITY = {
  critical: 1,
  warning: 2,
  healthy: 3,
  empty: 4,
};

const STATUS_GRADIENTS = {
  healthy: 'linear-gradient(145deg, #34d399 0%, #10b981 55%, #059669 100%)',
  warning: 'linear-gradient(145deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%)',
  critical: 'linear-gradient(145deg, #fb7185 0%, #f43f5e 55%, #e11d48 100%)',
  empty: 'linear-gradient(145deg, #94a3b8 0%, #64748b 55%, #475569 100%)',
};

const STATUS_RINGS = {
  healthy: 'ring-emerald-300/60',
  warning: 'ring-amber-300/60',
  critical: 'ring-rose-300/60',
  empty: 'ring-slate-300/50',
};

const STATUS_DOT = {
  healthy: '#10b981',
  warning: '#f59e0b',
  critical: '#f43f5e',
  empty: '#64748b',
};

function isDeviceOnline(lastSeen) {
  if (!lastSeen) return false;
  const age = Date.now() - new Date(lastSeen).getTime();
  return age < 5 * 60 * 1000;
}

function getMetricStatus(device, metric) {
  // Collection-marked critical devices always surface as critical on honeycomb cards
  if (device.collection_critical) return 'critical';

  if (metric === 'cpu') {
    const v = device.cpu ?? device.cpu_usage ?? 0;
    if (v === 0 && !device.cpu) return 'empty';
    if (v < 50) return 'healthy';
    if (v < 80) return 'warning';
    return 'critical';
  }
  if (metric === 'ram') {
    const v = device.ram ?? device.memory_usage ?? 0;
    if (v === 0 && !device.ram) return 'empty';
    if (v < 50) return 'healthy';
    if (v < 80) return 'warning';
    return 'critical';
  }
  if (metric === 'storage') {
    const v = device.storage ?? device.disk_usage ?? 0;
    if (v === 0 && device.storage === undefined && device.disk_usage === undefined) return 'empty';
    if (v < 50) return 'healthy';
    if (v < 80) return 'warning';
    return 'critical';
  }
  if (metric === 'status') {
    if (!isDeviceOnline(device.last_seen)) return 'critical';
    if (device.status === 'critical') return 'critical';
    const age = device.last_seen ? Date.now() - new Date(device.last_seen).getTime() : Infinity;
    if (age > 30 * 60 * 1000) return 'warning';
    return 'healthy';
  }
  if (metric === 'baseline') {
    const c = (device.compliance_status || '').toLowerCase();
    if (!c) return 'empty';
    if (c === 'compliant') return 'healthy';
    if (c === 'non_compliant' || c === 'non-compliant') return 'critical';
    return 'warning';
  }
  return 'empty';
}

function getMetricValue(device, metric) {
  if (metric === 'cpu') return `${device.cpu ?? device.cpu_usage ?? 0}%`;
  if (metric === 'ram') return `${device.ram ?? device.memory_usage ?? 0}%`;
  if (metric === 'storage') return `${device.storage ?? device.disk_usage ?? 0}%`;
  if (metric === 'status') {
    if (device.collection_critical) return 'Critical (marked)';
    return isDeviceOnline(device.last_seen) ? 'Online' : 'Offline';
  }
  if (metric === 'baseline') return device.compliance_status || 'Unknown';
  return '';
}

function getStatusDisplayLabel(status, metric, device) {
  if (device?.collection_critical) return metric === 'status' ? 'Critical (marked)' : 'Critical';
  if (metric === 'status') {
    if (status === 'healthy') return 'Online';
    if (status === 'critical') return 'Offline';
    if (status === 'warning') return 'Warning';
    return 'Unknown';
  }
  if (status === 'healthy') return 'Healthy';
  if (status === 'warning') return 'Warning';
  if (status === 'critical') return 'Critical';
  return 'Unknown';
}

function getMetricLabel(metricId) {
  return METRIC_OPTIONS.find((m) => m.id === metricId)?.label ?? 'Status';
}

function getDeviceShortCode(device) {
  const name = String(device.name || device.hostname || device.device_id || device.id || 'DV');
  const cleaned = name.replace(/[^a-zA-Z0-9]/g, '');
  if (cleaned.length >= 2) return cleaned.slice(0, 2).toUpperCase();
  return name.slice(0, 2).toUpperCase() || 'DV';
}

function getDeviceDisplayName(device) {
  return device.name || device.hostname || device.device_id || device.id || 'Device';
}

/** Check if all devices in the group have the same numeric value for metric (suggests placeholder/aggregate). */
function allSameNumericValue(devices, metric) {
  if (!['cpu', 'ram', 'storage'].includes(metric) || !devices.length) return false;
  const raw = (d) => {
    if (metric === 'cpu') return d.cpu ?? d.cpu_usage ?? 0;
    if (metric === 'ram') return d.ram ?? d.memory_usage ?? 0;
    if (metric === 'storage') return d.storage ?? d.disk_usage ?? 0;
    return 0;
  };
  const first = raw(devices[0]);
  return devices.every((d) => raw(d) === first);
}

function getHexConfig(deviceCount) {
  if (deviceCount <= 2) return { w: 72, h: 80, gap: 8 };
  if (deviceCount <= 4) return { w: 64, h: 72, gap: 8 };
  if (deviceCount <= 8) return { w: 56, h: 64, gap: 7 };
  if (deviceCount <= 15) return { w: 48, h: 55, gap: 6 };
  if (deviceCount <= 25) return { w: 42, h: 48, gap: 5 };
  if (deviceCount <= 40) return { w: 36, h: 42, gap: 4 };
  if (deviceCount <= 60) return { w: 30, h: 35, gap: 4 };
  return { w: 28, h: 32, gap: 3 };
}

/**
 * Body height for the honeycomb area — proportional to device count / canvas size.
 * Small groups stay compact; larger groups grow up to a soft cap.
 */
function getHoneycombBodyHeight(deviceCount, canvasHeight) {
  const pad = deviceCount <= 2 ? 16 : deviceCount <= 5 ? 20 : 24;
  const content = canvasHeight + pad;
  let floor = 120;
  if (deviceCount <= 2) floor = 130;
  else if (deviceCount <= 5) floor = 160;
  else if (deviceCount <= 15) floor = 200;
  else if (deviceCount <= 30) floor = 260;
  else floor = 280;
  // Size to real honeycomb height; never clip. Absolute body cap only for huge layouts.
  return Math.min(400, Math.max(floor, content));
}

/** Compact / diamond layouts for 1–5 devices */
function getDiamondRowDistribution(n) {
  if (n <= 0) return [];
  if (n === 1) return [1];
  if (n === 2) return [2];
  if (n === 3) return [2, 1];
  if (n === 4) return [1, 2, 1];
  if (n === 5) return [2, 3];
  return [];
}

/**
 * Point-top hex geometry positions.
 * Rows are centered on the canvas so shorter rows nest into longer ones ([3,4,3]).
 * Equal-length consecutive rows get half-step stagger for interlocking.
 */
function computeHoneycombLayout(items, hexConfig) {
  const { w: HEX_W, h: HEX_H, gap: HEX_GAP } = hexConfig;
  const n = items.length;
  if (n === 0) {
    return { positions: [], canvasWidth: 0, canvasHeight: 0, hexConfig };
  }

  const rowSizes = getRowSizesForCount(n);
  const rows = [];
  let idx = 0;
  for (const size of rowSizes) {
    rows.push(items.slice(idx, idx + size));
    idx += size;
  }

  const horizSpacing = HEX_W * 0.86 + HEX_GAP;
  const vertSpacing = HEX_H * 0.75 + HEX_GAP;
  const maxLen = Math.max(...rowSizes, 1);

  // Odd-row stagger only when consecutive rows share equal length (true honeycomb columns)
  const needsOddStagger =
    rows.length > 1 &&
    rowSizes.some((len, i) => i > 0 && len === rowSizes[i - 1] && len >= 2);

  const baseWidth = (maxLen - 1) * horizSpacing + HEX_W;
  const canvasWidth = baseWidth + (needsOddStagger ? horizSpacing / 2 : 0);
  const canvasHeight = (rows.length - 1) * vertSpacing + HEX_H;

  const positions = [];
  rows.forEach((row, rowIdx) => {
    const L = row.length;
    const rowWidth = L <= 1 ? HEX_W : (L - 1) * horizSpacing + HEX_W;
    let startX = (baseWidth - rowWidth) / 2;
    if (needsOddStagger && rowIdx % 2 === 1) {
      startX += horizSpacing / 2;
    }
    const y = rowIdx * vertSpacing;
    row.forEach((item, colIdx) => {
      positions.push({
        item,
        x: startX + colIdx * horizSpacing,
        y,
        key: item.key,
      });
    });
  });

  return { positions, canvasWidth, canvasHeight, hexConfig, rowCount: rows.length };
}

/** Hexagon layout for 6+ devices */
function getHexagonRowDistribution(n) {
  if (n <= 0) return [];
  if (n === 1) return [1];
  if (n === 2) return [2];
  if (n === 3) return [2, 1];
  if (n === 4) return [1, 2, 1];
  if (n === 5) return [2, 2, 1];
  if (n === 6) return [1, 2, 2, 1];
  if (n === 7) return [2, 3, 2];
  if (n === 8) return [3, 2, 3];
  if (n === 9) return [3, 3, 3];
  if (n === 10) return [3, 4, 3];
  if (n === 11) return [3, 4, 4];
  if (n === 12) return [4, 4, 4];
  if (n === 13) return [3, 4, 4, 2];
  if (n === 14) return [3, 4, 4, 3];
  if (n === 15) return [4, 4, 4, 3];
  if (n === 16) return [4, 4, 4, 4];
  const w = Math.max(2, Math.ceil(Math.sqrt(n)));
  const half = [];
  for (let i = 1; i <= w; i++) half.push(i);
  const full = [...half, ...half.slice(0, -1).reverse()];
  let sum = full.reduce((a, b) => a + b, 0);
  const result = [...full];
  if (sum > n) {
    for (let i = result.length - 1; sum > n && i >= 0; i--) {
      const cut = Math.min(result[i], sum - n);
      result[i] -= cut;
      sum -= cut;
    }
  } else if (sum < n) {
    const mid = Math.floor(result.length / 2);
    result[mid] += n - sum;
  }
  return result.filter((x) => x > 0);
}

function getRowSizesForCount(n) {
  if (n <= GRID_DIAMOND_THRESHOLD) return getDiamondRowDistribution(n);
  return getHexagonRowDistribution(n);
}

function countStatuses(devices, metric) {
  const h = { healthy: 0, warning: 0, critical: 0, empty: 0 };
  devices.forEach((d) => {
    const s = getMetricStatus(d, metric);
    h[s] = (h[s] || 0) + 1;
  });
  return h;
}

function sortByHealthPriority(devices, metric) {
  return [...devices].sort((a, b) => {
    const pa = STATUS_PRIORITY[getMetricStatus(a, metric)] ?? 4;
    const pb = STATUS_PRIORITY[getMetricStatus(b, metric)] ?? 4;
    if (pa !== pb) return pa - pb;
    const na = getDeviceDisplayName(a);
    const nb = getDeviceDisplayName(b);
    return na.localeCompare(nb);
  });
}

/**
 * Exception-first preview for large groups.
 * Returns { visibleDevices, hiddenCount, isPreview, isCompactSummary }
 */
function getVisibleDevicesForCard(devices, metric) {
  const total = devices.length;
  if (total <= MAX_HEXES_PER_CARD) {
    return {
      visibleDevices: devices,
      hiddenCount: 0,
      isPreview: false,
      isCompactSummary: false,
    };
  }

  const sorted = sortByHealthPriority(devices, metric);
  const critical = [];
  const warning = [];
  const healthy = [];
  const unknown = [];

  sorted.forEach((d) => {
    const s = getMetricStatus(d, metric);
    if (s === 'critical') critical.push(d);
    else if (s === 'warning') warning.push(d);
    else if (s === 'healthy') healthy.push(d);
    else unknown.push(d);
  });

  // Very large: show unhealthy focus only
  if (total > LARGE_GROUP_THRESHOLD) {
    const unhealthy = [...critical, ...warning];
    const healthySample = healthy.slice(0, Math.min(5, HEALTHY_SAMPLE_IN_PREVIEW));
    const visible = [...unhealthy, ...healthySample].slice(0, MAX_HEXES_PER_CARD);
    return {
      visibleDevices: visible,
      hiddenCount: Math.max(0, total - visible.length),
      isPreview: true,
      isCompactSummary: true,
    };
  }

  // 61–200: critical + warning first, then healthy sample
  let remaining = MAX_HEXES_PER_CARD;
  const visible = [];
  const takeCritical = critical.slice(0, remaining);
  visible.push(...takeCritical);
  remaining -= takeCritical.length;
  const takeWarning = warning.slice(0, remaining);
  visible.push(...takeWarning);
  remaining -= takeWarning.length;
  const takeHealthy = healthy.slice(0, Math.min(HEALTHY_SAMPLE_IN_PREVIEW, remaining));
  visible.push(...takeHealthy);
  remaining -= takeHealthy.length;
  if (remaining > 0) {
    visible.push(...unknown.slice(0, remaining));
  }

  return {
    visibleDevices: visible,
    hiddenCount: Math.max(0, total - visible.length),
    isPreview: true,
    isCompactSummary: false,
  };
}

function formatHealthSummary(counts, metric) {
  const parts = [];
  const healthyLabel = metric === 'status' ? 'Online' : 'Healthy';
  const criticalLabel = metric === 'status' ? 'Offline' : 'Critical';
  if (counts.healthy > 0) parts.push(`${counts.healthy} ${healthyLabel}`);
  if (counts.warning > 0) parts.push(`${counts.warning} Warning`);
  if (counts.critical > 0) parts.push(`${counts.critical} ${criticalLabel}`);
  if (counts.empty > 0 && parts.length === 0) parts.push(`${counts.empty} Unknown`);
  return parts.join(' • ') || 'No status data';
}

function loadFromStorage(key, fallback) {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
}

function friendlyNameFromId(id) {
  if (!id || typeof id !== 'string') return 'Collection';
  if (id === 'Unassigned') return id;
  if (id.startsWith('dept-custom-')) return id.replace(/^dept-custom-\d+-/, 'Collection ');
  if (id.startsWith('folder-')) return id.replace(/^folder-/, '').replace(/-/g, ' ');
  return id;
}

/* -------------------------------------------------------------------------- */
/* Internal presentational components                                         */
/* -------------------------------------------------------------------------- */

const DeviceHexCell = memo(function DeviceHexCell({
  device,
  metricFilter,
  hexConfig,
  onDeviceClick,
}) {
  const { w, h } = hexConfig;
  const status = getMetricStatus(device, metricFilter);
  const isEmpty = status === 'empty' && ['cpu', 'ram', 'storage'].includes(metricFilter);
  const value = isEmpty ? 'No data' : getMetricValue(device, metricFilter);
  const statusLabel = getStatusDisplayLabel(status, metricFilter, device);
  const metricLabel = getMetricLabel(metricFilter);
  const name = getDeviceDisplayName(device);
  const shortCode = getDeviceShortCode(device);
  const fontSize = w <= 32 ? 8 : w <= 42 ? 9 : 10;

  const osqueryHint =
    !device.has_osquery && ['cpu', 'ram', 'storage'].includes(metricFilter)
      ? '\nOsquery: no data — enable in Settings → Osquery.'
      : '';

  const lastSeenLine = device.last_seen
    ? `\nLast seen: ${new Date(device.last_seen).toLocaleString()}`
    : '';

  const tooltip = `${name}\n${metricLabel}: ${value}\nStatus: ${statusLabel}${lastSeenLine}${osqueryHint}`;
  const ariaLabel = `Device ${name}, status ${statusLabel}, metric ${metricLabel}: ${value}`;

  const activate = () => onDeviceClick?.(device);

  return (
    <div
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      className={`relative flex items-center justify-center cursor-pointer shrink-0
        transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1
        ring-1 ${STATUS_RINGS[status]}`}
      style={{
        width: w,
        height: h,
        clipPath: HEX_CLIP,
        background: STATUS_GRADIENTS[status],
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      title={tooltip}
    >
      <span className="flex flex-col items-center justify-center gap-0.5 text-white select-none">
        <span
          className="rounded-full shadow-sm"
          style={{
            width: Math.max(4, Math.round(w * 0.12)),
            height: Math.max(4, Math.round(w * 0.12)),
            backgroundColor: 'rgba(255,255,255,0.9)',
          }}
        />
        {w >= 36 && (
          <span className="font-semibold tracking-wide opacity-95" style={{ fontSize }}>
            {shortCode}
          </span>
        )}
      </span>
    </div>
  );
});

const MoreDevicesHex = memo(function MoreDevicesHex({ count, hexConfig, onClick }) {
  const { w, h } = hexConfig;
  const label = count > 999 ? '+999' : `+${count}`;
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="relative flex items-center justify-center cursor-pointer shrink-0
        transition-transform duration-150 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
        ring-1 ring-slate-300/70 bg-slate-100 text-slate-600"
      style={{
        width: w,
        height: h,
        clipPath: HEX_CLIP,
        background: 'linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)',
      }}
      role="button"
      tabIndex={0}
      aria-label={`${count} more devices. View all health status.`}
      title={`${count} more devices\nClick to view all health status`}
    >
      <span className="text-[10px] font-bold text-slate-600 select-none">{label}</span>
    </div>
  );
});

function MetricPillSelector({ metric, onChange }) {
  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-thin"
      role="group"
      aria-label="Health metric filter"
      onClick={(e) => e.stopPropagation()}
    >
      {METRIC_OPTIONS.map((m) => {
        const Icon = m.icon;
        const active = metric === m.id;
        return (
          <button
            key={m.id}
            type="button"
            aria-pressed={active}
            onClick={(e) => {
              e.stopPropagation();
              onChange(m.id);
            }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full border whitespace-nowrap shrink-0 transition-colors ${
              active
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
            }`}
            title={`Color by ${m.label}`}
          >
            <Icon size={12} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

function HoneycombLegend({ counts, metric }) {
  const items = [
    {
      key: 'healthy',
      count: counts.healthy,
      label: metric === 'status' ? 'Online' : 'Healthy',
      color: STATUS_DOT.healthy,
    },
    {
      key: 'warning',
      count: counts.warning,
      label: 'Warning',
      color: STATUS_DOT.warning,
    },
    {
      key: 'critical',
      count: counts.critical,
      label: metric === 'status' ? 'Offline' : 'Critical',
      color: STATUS_DOT.critical,
    },
  ].filter((i) => i.count > 0);

  if (items.length === 0 && counts.empty > 0) {
    items.push({ key: 'empty', count: counts.empty, label: 'Unknown', color: STATUS_DOT.empty });
  }

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] text-slate-600">
      {items.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 shrink-0"
            style={{ clipPath: HEX_CLIP, backgroundColor: item.color }}
          />
          <span className="font-semibold tabular-nums">{item.count}</span>
          <span className="text-slate-500">{item.label}</span>
        </span>
      ))}
    </div>
  );
}

function HoneycombGrid({
  positions,
  canvasWidth,
  canvasHeight,
  hexConfig,
  metricFilter,
  onDeviceClick,
  onMoreClick,
}) {
  if (!positions.length) return null;

  return (
    <div className="w-full flex items-center justify-center overflow-hidden">
      <div
        className="relative shrink-0"
        style={{
          width: canvasWidth,
          height: canvasHeight,
          maxWidth: '100%',
        }}
      >
        {positions.map(({ item, x, y, key }) => (
          <div
            key={key}
            className="absolute"
            style={{ left: x, top: y, width: hexConfig.w, height: hexConfig.h }}
          >
            {item.type === 'more' ? (
              <MoreDevicesHex
                count={item.moreCount}
                hexConfig={hexConfig}
                onClick={onMoreClick}
              />
            ) : (
              <DeviceHexCell
                device={item.device}
                metricFilter={metricFilter}
                hexConfig={hexConfig}
                onDeviceClick={onDeviceClick}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HoneycombCard({
  group,
  metric,
  onMetricChange,
  onGroupClick,
  onDeviceClick,
  onViewAll,
}) {
  const allDevices = useMemo(() => group.devices || [], [group.devices]);
  const fullCounts = useMemo(() => countStatuses(allDevices, metric), [allDevices, metric]);
  const visibility = useMemo(
    () => getVisibleDevicesForCard(allDevices, metric),
    [allDevices, metric]
  );

  const { visibleDevices, hiddenCount, isPreview, isCompactSummary } = visibility;
  const displayItemCount = visibleDevices.length + (hiddenCount > 0 ? 1 : 0);

  const layout = useMemo(() => {
    const items = visibleDevices.map((d) => ({
      type: 'device',
      device: d,
      key: d.id || d.device_id,
    }));
    if (hiddenCount > 0) {
      items.push({ type: 'more', moreCount: hiddenCount, key: '__more__' });
    }
    const hexConfig = getHexConfig(Math.max(items.length, 1));
    return computeHoneycombLayout(items, hexConfig);
  }, [visibleDevices, hiddenCount]);

  const bodyHeight =
    allDevices.length === 0
      ? 100
      : getHoneycombBodyHeight(Math.max(displayItemCount, 1), layout.canvasHeight);

  const metricLabel = getMetricLabel(metric);
  const summaryText = formatHealthSummary(fullCounts, metric);
  const previewBannerHeight = isPreview ? 28 : 0;
  const sameValueHint =
    ['cpu', 'ram', 'storage'].includes(metric) && allSameNumericValue(allDevices, metric);

  return (
    <div
      className="bg-white rounded-xl border border-slate-200/90 shadow-sm overflow-hidden flex flex-col min-w-0"
      style={{ maxHeight: 520 }}
    >
      {/* Header: group name, count, metric pill, summary */}
      <div
        className="px-4 pt-3 pb-2 border-b border-slate-100 bg-gradient-to-b from-slate-50/80 to-white cursor-pointer shrink-0"
        onClick={() => onGroupClick?.(group)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onGroupClick?.(group);
          }
        }}
        aria-label={`Open group ${group.name}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{group.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {allDevices.length} {allDevices.length === 1 ? 'device' : 'devices'}
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
            {metricLabel}
          </span>
        </div>
        <p className="text-[11px] text-slate-600 mt-1.5 truncate" title={summaryText}>
          {summaryText}
        </p>
      </div>

      {/* Horizontal metric pills */}
      <div className="px-3 py-2 border-b border-slate-100 shrink-0">
        <MetricPillSelector metric={metric} onChange={onMetricChange} />
      </div>

      {/* Honeycomb body — height from canvas + padding, not a fixed large min-height */}
      <div
        className="px-3 flex flex-col items-center justify-center overflow-hidden"
        style={{
          height: bodyHeight + previewBannerHeight + (sameValueHint ? 28 : 0),
          minHeight: bodyHeight,
        }}
      >
        {allDevices.length === 0 ? (
          <div className="text-sm text-slate-500 italic text-center">No devices</div>
        ) : (
          <>
            {isPreview && (
              <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1 mb-1.5 text-center w-full max-w-sm shrink-0">
                {isCompactSummary
                  ? 'Large group — showing critical and warning devices first'
                  : 'Showing critical and warning devices first'}
              </p>
            )}

            <HoneycombGrid
              positions={layout.positions}
              canvasWidth={layout.canvasWidth}
              canvasHeight={layout.canvasHeight}
              hexConfig={layout.hexConfig}
              metricFilter={metric}
              onDeviceClick={onDeviceClick}
              onMoreClick={() => onViewAll?.(group)}
            />

            {sameValueHint && (
              <p
                className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 text-center max-w-xs shrink-0"
                title="Install or enable the Device API agent or Osquery on each device for per-device metrics."
              >
                Same value for all — ensure per-device metrics are reported.
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer legend + view all */}
      <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50/40 shrink-0">
        <HoneycombLegend counts={fullCounts} metric={metric} />
        {(isPreview || allDevices.length > MAX_HEXES_PER_CARD) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll?.(group);
            }}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            View all health status
            <ExternalLink size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main export                                                                */
/* -------------------------------------------------------------------------- */

export default function HealthHoneycombView({
  devices = [],
  collections: propsCollections,
  folders: propsFolders,
  complianceData = [],
  onCellClick,
  onDeviceClick,
  /** Optional: navigate/filter to full health list for a group. Falls back to onCellClick. */
  onViewAllHealth,
  /** Whether the backend API is connected (from health check). */
  backendConnected = null,
  /** Whether we have real metrics (CPU/RAM/Storage) from API/Osquery; if false, values may be placeholders. */
  hasRealMetrics = null,
  /** When true, CPU/RAM/Storage are from Osquery (telemetry). When false, from Device API (Go agent/backend). */
  metricsFromOsquery = null,
}) {
  const [viewMode, setViewMode] = useState('collection');
  const [metricByGroup, setMetricByGroup] = useState({}); // groupId -> metric

  const complianceByDevice = useMemo(() => {
    const m = new Map();
    (complianceData || []).forEach((c) => {
      const id = c.device_id || c.deviceId || c.id;
      if (id) m.set(id, c.compliance_status || c.complianceStatus);
    });
    return m;
  }, [complianceData]);

  const devicesWithCompliance = useMemo(() => {
    return devices.map((d) => ({
      ...d,
      compliance_status: d.compliance_status ?? complianceByDevice.get(d.id || d.device_id),
    }));
  }, [devices, complianceByDevice]);

  const collections = useMemo(() => {
    const custom = propsCollections != null ? propsCollections : loadFromStorage(STORAGE_COLLECTIONS, []);
    const deviceDepts = [
      ...new Set(
        devicesWithCompliance
          .map((d) => d.dept || d.device_metadata?.department || 'Unassigned')
          .filter(Boolean)
      ),
    ];
    const customById = new Map((Array.isArray(custom) ? custom : []).map((c) => [c.id, c]));
    const map = new Map();
    deviceDepts.forEach((id) => {
      const c = customById.get(id);
      const displayName = c?.name ?? c?.label ?? c?.title ?? friendlyNameFromId(id);
      map.set(id, {
        id,
        name: displayName,
        folderId: c?.folderId ?? null,
        criticalDeviceIds: Array.isArray(c?.criticalDeviceIds) ? c.criticalDeviceIds : [],
      });
    });
    (Array.isArray(custom) ? custom : []).forEach((c) => {
      if (!map.has(c.id)) {
        const displayName = c.name ?? c.label ?? c.title ?? friendlyNameFromId(c.id);
        map.set(c.id, {
          id: c.id,
          name: displayName,
          folderId: c.folderId ?? null,
          criticalDeviceIds: Array.isArray(c.criticalDeviceIds) ? c.criticalDeviceIds : [],
        });
      }
    });
    return Array.from(map.values());
  }, [propsCollections, devicesWithCompliance]);

  const foldersList = useMemo(() => {
    if (propsFolders != null) return propsFolders;
    return loadFromStorage(STORAGE_FOLDERS, [{ id: 'folder-departments-default', name: 'Departments' }]);
  }, [propsFolders]);

  const groups = useMemo(() => {
    const criticalByCollection = new Map();
    collections.forEach((col) => {
      criticalByCollection.set(
        col.id,
        new Set((col.criticalDeviceIds || []).map(String))
      );
    });

    const withCriticalFlag = (deviceList, collectionId) => {
      const criticalSet = criticalByCollection.get(collectionId) || new Set();
      return deviceList.map((d) => {
        const id = String(d.id || d.device_id || '');
        return {
          ...d,
          collection_critical: criticalSet.has(id),
        };
      });
    };

    if (viewMode === 'platform') {
      return [
        { id: 'plat-windows', name: 'Windows' },
        { id: 'plat-linux', name: 'Linux' },
        { id: 'plat-macos', name: 'macOS' },
        { id: 'plat-android', name: 'Android' },
        { id: 'plat-ios', name: 'iOS' },
      ].map((p) => {
        const matched = devicesWithCompliance.filter((d) => deviceMatchesOS(d, p.id));
        // Platform view: device is critical if marked in its own collection
        return {
          ...p,
          devices: matched.map((d) => {
            const deptId = d.dept || d.device_metadata?.department;
            const criticalSet = criticalByCollection.get(deptId) || new Set();
            const id = String(d.id || d.device_id || '');
            return { ...d, collection_critical: criticalSet.has(id) };
          }),
        };
      });
    }
    if (viewMode === 'folder') {
      return foldersList.map((folder) => {
        const folderCollectionIds = collections
          .filter((c) => c.folderId === folder.id)
          .map((c) => c.id);
        const folderDevices = devicesWithCompliance.filter((d) =>
          folderCollectionIds.includes(d.dept)
        );
        return {
          id: folder.id,
          name: folder.name,
          devices: folderDevices.map((d) => {
            const criticalSet = criticalByCollection.get(d.dept) || new Set();
            const id = String(d.id || d.device_id || '');
            return { ...d, collection_critical: criticalSet.has(id) };
          }),
        };
      });
    }
    return collections.map((col) => ({
      id: col.id,
      name: col.name,
      devices: withCriticalFlag(
        devicesWithCompliance.filter((d) => d.dept === col.id),
        col.id
      ),
    }));
  }, [viewMode, devicesWithCompliance, collections, foldersList]);

  const displayGroups = groups.filter((g) => g.devices.length > 0 || viewMode === 'platform');

  const getMetricForGroup = useCallback(
    (groupId) => metricByGroup[groupId] || 'status',
    [metricByGroup]
  );

  const setMetricForGroup = useCallback((groupId, metric) => {
    setMetricByGroup((prev) => ({ ...prev, [groupId]: metric }));
  }, []);

  const handleGroupClick = useCallback(
    (g) => {
      onCellClick?.(g);
    },
    [onCellClick]
  );

  const handleDeviceClick = useCallback(
    (device, group) => {
      (onDeviceClick ?? onCellClick)?.(device, group);
    },
    [onDeviceClick, onCellClick]
  );

  const handleViewAll = useCallback(
    (group) => {
      if (typeof onViewAllHealth === 'function') {
        onViewAllHealth(group);
        return;
      }
      // Fallback: reuse group click (opens collection/platform selection in dashboard)
      if (typeof onCellClick === 'function') {
        onCellClick(group);
        return;
      }
      // TODO: Wire to Devices page filtered by collection/folder/platform when routing is available.
      // eslint-disable-next-line no-console
      console.info('[HealthHoneycombView] View all health status for group:', group?.id, group?.name);
    },
    [onViewAllHealth, onCellClick]
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold text-slate-700 flex items-center gap-2">
            <Layers size={18} />
            Health by{' '}
            {viewMode === 'collection' ? 'Collection' : viewMode === 'folder' ? 'Folder' : 'Platform'}
          </h2>
          {(backendConnected !== null || hasRealMetrics !== null || metricsFromOsquery !== null) && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {backendConnected !== null && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-medium ${
                    backendConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                  }`}
                  title={backendConnected ? 'Backend API is reachable' : 'Backend API unreachable'}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      backendConnected ? 'bg-emerald-500' : 'bg-rose-500'
                    }`}
                  />
                  Backend: {backendConnected ? 'Connected' : 'Disconnected'}
                </span>
              )}
              {hasRealMetrics !== null && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-medium ${
                    hasRealMetrics ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                  }`}
                  title={hasRealMetrics ? 'CPU/RAM/Storage from API or Osquery' : 'No metrics yet from agents'}
                >
                  Data: {hasRealMetrics ? 'Live' : 'No metrics yet'}
                </span>
              )}
              {metricsFromOsquery !== null && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-medium bg-violet-100 text-violet-700"
                  title={
                    metricsFromOsquery
                      ? 'CPU, RAM, Storage from Osquery'
                      : 'CPU, RAM, Storage from Device API'
                  }
                >
                  Metrics: {metricsFromOsquery ? 'Osquery' : 'Device API'}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setViewMode(opt.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                viewMode === opt.id
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {displayGroups.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 italic">
          No groups with devices. Add devices to collections or check folder structure.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
          {displayGroups.map((g) => (
            <HoneycombCard
              key={g.id}
              group={g}
              metric={getMetricForGroup(g.id)}
              onMetricChange={(m) => setMetricForGroup(g.id, m)}
              onGroupClick={handleGroupClick}
              onDeviceClick={(device) => handleDeviceClick(device, g)}
              onViewAll={handleViewAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}
